import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Receipt,
  CircleNotch,
  Warning,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { fetchMatters } from '@/lib/mattersApi';
import { api, getApiErrorMessage } from '@/lib/api';
import { createFeeNote, validateFeeNote, type FeeNoteDetail } from '@/lib/feeNotesApi';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Optional callback after a fee note is created — list query auto-invalidates regardless. */
  onSave?: (created: FeeNoteDetail) => void;
  /** Pre-select a matter (e.g. opened from CaseDetailPage). */
  presetMatterId?: string;
}

interface FeeRecordRow {
  id: string;
  feeType: string;
  description: string;
  feeDate: string;
  durationMinutes: number | null;
  rate: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
  isBillable: boolean;
  isWrittenOff: boolean;
  feeNoteId: string | null;
  barrister: { id: string; user: { firstName: string; lastName: string } } | null;
}

interface FeeRecordListResponse {
  data: {
    feeRecords: FeeRecordRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

async function fetchUnbilledFeeRecords(matterId: string): Promise<FeeRecordRow[]> {
  const res = await api.get<FeeRecordListResponse>('/fees', {
    params: {
      caseId: matterId,
      unbilled: 'true',
      isBillable: 'true',
      limit: 100,
    },
  });
  return res.data.data.feeRecords;
}

const fmtGBP = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(n);

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(base: string, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function NewFeeNoteModal({ open, onClose, onSave, presetMatterId }: Props) {
  const qc = useQueryClient();
  const [matterId, setMatterId] = useState<string>(presetMatterId ?? '');
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(new Set());
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(addDaysIso(todayIso(), 30));
  const [notes, setNotes] = useState('');

  // Reset every time the modal opens.
  useEffect(() => {
    if (open) {
      setMatterId(presetMatterId ?? '');
      setSelectedRecordIds(new Set());
      setIssueDate(todayIso());
      setDueDate(addDaysIso(todayIso(), 30));
      setNotes('');
    }
  }, [open, presetMatterId]);

  // Page scroll lock: <ScrollLock /> inside the scrim below. It replaced a local
  // `overflow: hidden` here, which iOS Safari ignored for touch scrolling.

  const mattersQ = useQuery({
    queryKey: ['matters-picker-fee-note'],
    queryFn: () => fetchMatters().then((r) => r.matters),
    enabled: open && !presetMatterId,
    staleTime: 0,
  });

  const recordsQ = useQuery({
    queryKey: ['fee-records', 'unbilled', matterId],
    queryFn: () => fetchUnbilledFeeRecords(matterId),
    enabled: open && Boolean(matterId),
  });

  const records = recordsQ.data ?? [];
  const selectedIds = useMemo(() => Array.from(selectedRecordIds), [selectedRecordIds]);

  const validationQ = useQuery({
    queryKey: ['fee-note-validate', matterId, selectedIds],
    queryFn: () => validateFeeNote({ caseId: matterId, feeRecordIds: selectedIds }),
    enabled: Boolean(matterId) && selectedRecordIds.size > 0,
    staleTime: 0,
  });

  const blockingErrors = validationQ.data?.errors.filter((e) => e.severity === 'blocking') ?? [];
  const warningErrors = validationQ.data?.errors.filter((e) => e.severity === 'warning') ?? [];

  const totals = useMemo(() => {
    const selected = records.filter((r) => selectedRecordIds.has(r.id));
    const subtotal = selected.reduce((acc, r) => acc + Number(r.amount), 0);
    const vat = selected.reduce((acc, r) => acc + Number(r.vatAmount), 0);
    const total = selected.reduce((acc, r) => acc + Number(r.totalAmount), 0);
    return { count: selected.length, subtotal, vat, total };
  }, [records, selectedRecordIds]);

  const toggleRecord = (id: string) => {
    setSelectedRecordIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedRecordIds(new Set(records.map((r) => r.id)));
  };

  const clearAll = () => {
    setSelectedRecordIds(new Set());
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!matterId) throw new Error('Pick a matter first');
      if (selectedRecordIds.size === 0) throw new Error('Select at least one fee record');
      return createFeeNote({
        caseId: matterId,
        feeRecordIds: Array.from(selectedRecordIds),
        issueDate,
        dueDate,
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: (created) => {
      toast.success(`Fee note ${created.feeNoteNumber} created — total ${fmtGBP(Number(created.total))}`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      qc.invalidateQueries({ queryKey: ['fee-records'] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary', matterId] });
      onSave?.(created);
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create fee note')),
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Receipt size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              New fee note
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Roll selected unbilled fee records into a new invoice. Number is auto-generated.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {/* Matter picker */}
          {!presetMatterId && (
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Matter
              </label>
              <select
                value={matterId}
                onChange={(e) => {
                  setMatterId(e.target.value);
                  setSelectedRecordIds(new Set());
                }}
                disabled={saveMut.isPending}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— Select a matter —</option>
                {mattersQ.data?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.number} · {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Records picker */}
          {matterId && (
            <div>
              <div className="mb-1 flex items-end justify-between">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Unbilled fee records
                </label>
                {records.length > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      onClick={selectAll}
                      type="button"
                      className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                    >
                      Select all
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button
                      onClick={clearAll}
                      type="button"
                      className="font-medium text-muted-foreground hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {recordsQ.isLoading && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
                  <CircleNotch size={12} className="animate-spin" /> Loading unbilled records…
                </div>
              )}

              {recordsQ.data && records.length === 0 && (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-3 text-xs text-amber-800 dark:text-amber-300">
                  <Warning size={12} weight="fill" />
                  No unbilled fee records on this matter — log time / a fee record first via Activities or the matter detail page.
                </div>
              )}

              {records.length > 0 && (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-muted/30 text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium" style={{ width: 28 }}></th>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Description</th>
                        <th className="px-3 py-2 text-left font-medium">Barrister</th>
                        <th className="px-3 py-2 text-right font-medium">Amount</th>
                        <th className="px-3 py-2 text-right font-medium">VAT</th>
                        <th className="px-3 py-2 text-right font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {records.map((r) => {
                        const checked = selectedRecordIds.has(r.id);
                        return (
                          <tr
                            key={r.id}
                            onClick={() => toggleRecord(r.id)}
                            className={`cursor-pointer ${checked ? 'bg-brand-50/60 dark:bg-brand-900/20' : 'hover:bg-muted/20'}`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRecord(r.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
                              />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-xs">
                              {new Date(r.feeDate).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-3 py-2 capitalize">{r.feeType.replace(/_/g, ' ')}</td>
                            <td className="max-w-xs truncate px-3 py-2">{r.description}</td>
                            <td className="px-3 py-2 text-xs">
                              {r.barrister
                                ? `${r.barrister.user.firstName} ${r.barrister.user.lastName}`
                                : '—'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right">
                              {fmtGBP(Number(r.amount))}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right">
                              {fmtGBP(Number(r.vatAmount))}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
                              {fmtGBP(Number(r.totalAmount))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Dates + notes (only meaningful once matter+records picked) */}
          {matterId && records.length > 0 && (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                    Issue date
                  </label>
                  <Input
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    disabled={saveMut.isPending}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                    Due date
                  </label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={saveMut.isPending}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Notes <span className="font-normal normal-case text-muted-foreground/70">(footer text — appears on the PDF)</span>
                </label>
                <ExpandableTextarea
                  label="Notes"
                  value={notes}
                  onChange={setNotes}
                  placeholder="Optional — e.g. payment instructions, reference, agreed terms"
                  rows={2}
                  maxLength={2000}
                  disabled={saveMut.isPending}
                />
              </div>

              {/* Live totals */}
              <div className="grid grid-cols-3 gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Subtotal · {totals.count} item{totals.count === 1 ? '' : 's'}</div>
                  <div className="text-sm font-semibold">{fmtGBP(totals.subtotal)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">VAT</div>
                  <div className="text-sm font-semibold">{fmtGBP(totals.vat)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Total</div>
                  <div className="text-sm font-semibold text-brand-700 dark:text-brand-400">{fmtGBP(totals.total)}</div>
                </div>
              </div>

              {/* Billing validation errors */}
              {blockingErrors.length > 0 && (
                <div className="space-y-1">
                  {blockingErrors.map((e) => (
                    <div
                      key={e.code}
                      className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10 px-3 py-2 text-xs text-red-800 dark:text-red-300"
                    >
                      <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
                      {e.message}
                    </div>
                  ))}
                </div>
              )}
              {warningErrors.length > 0 && (
                <div className="space-y-1">
                  {warningErrors.map((e) => (
                    <div
                      key={e.code}
                      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                    >
                      <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
                      {e.message}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !matterId || selectedRecordIds.size === 0 || blockingErrors.length > 0}
            className="gap-1.5"
          >
            {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
            Create fee note
          </Button>
        </div>
      </Card>
    </div>
  );
}
